/**
 * Demo-mode mutation handlers.
 *
 * Each handler fabricates the EXACT response envelope its real endpoint
 * returns (the consuming component checks `status === 'ok'` etc. — a generic
 * shape would break the UI mid-demo) and records side effects on the overlay
 * so follow-up GETs stay consistent (adapter.ts merges them).
 *
 * DELIBERATELY NOT COMPREHENSIVE: an unregistered mutation falls through to
 * the server and 403s (staff without X-Edit-On-Behalf) — that is the safety
 * backstop, never fabricate a fallback success here.
 *
 * Plan: knowledge/portal/plans/2026-07-16-portal-demo-mode.md
 */
import type { InternalAxiosRequestConfig } from 'axios'
import { demoOverlay, nextDemoId, type DemoAccident } from './overlay'

export interface DemoHandler {
  method: 'post' | 'put' | 'patch' | 'delete'
  match: (url: string) => boolean
  /** Fabricate the response body AND record overlay effects. */
  respond: (config: InternalAxiosRequestConfig) => unknown
}

/** Envelope used by the client_requests family + widget endpoints. */
function widgetOk(message: string, extra: Record<string, unknown> = {}) {
  return { status: 'ok', data: { message, ...extra }, error: null }
}

/** Parse a JSON-string / FormData / object request body into a plain object. */
export function parseDemoBody(
  config: InternalAxiosRequestConfig,
): Record<string, unknown> {
  const d = config.data
  if (!d) return {}
  if (typeof d === 'string') {
    try {
      return JSON.parse(d) as Record<string, unknown>
    } catch {
      return {}
    }
  }
  if (typeof FormData !== 'undefined' && d instanceof FormData) {
    const out: Record<string, unknown> = {}
    d.forEach((v, k) => {
      out[k] = v
    })
    return out
  }
  return d as Record<string, unknown>
}

/** Resolve a picked driver's display name from the roster harvest (adapter.ts).
 *  Unknown id (roster not fetched while demo was on) falls back to null — the
 *  row renders "—", the pre-harvest behavior. */
function demoDriverName(driverId: unknown): string | null {
  return driverId ? (demoOverlay.driverNames[String(driverId)] ?? null) : null
}

export const demoHandlers: DemoHandler[] = [
  {
    method: 'post',
    match: (url) => /\/v1\/fleet\/grounding\/employees\/[^/]+\/clear(?:\?|$)/.test(url),
    respond: (config) => {
      const reason = String(parseDemoBody(config).reason ?? '').trim()
      const now = new Date().toISOString()
      return {
        oos_grounded: false,
        clearance: {
          id: nextDemoId(), reason, release_reason: reason,
          created_at: now, quiet_until: now, released_at: now,
        },
      }
    },
  },
  {
    // Walkthrough "order MVR" resolution
    method: 'post',
    match: (u) => u.includes('/v1/client/requests/mvr'),
    respond: () =>
      widgetOk(
        'MVR ordered — the report files to the driver record automatically. (Demo — nothing was ordered.)',
        { status: 'executed' },
      ),
  },
  {
    // Multipart document upload (walkthrough + documents panel).
    // MUST be listed before the plain /documents matcher below.
    method: 'post',
    match: (u) => u.includes('/v1/client/requests/documents/upload'),
    respond: (cfg) => {
      const body = parseDemoBody(cfg)
      const file = body.file as File | undefined
      demoOverlay.uploadedDocs.push({
        driverId: String(body.employee_id ?? ''),
        fileType: String(body.file_type ?? ''),
        fileName: file?.name ?? 'document.pdf',
      })
      return widgetOk('Uploaded and filed to the driver record. (Demo — nothing was saved.)')
    },
  },
  {
    // "Request documents from the driver" (documents panel basket)
    method: 'post',
    match: (u) =>
      u.includes('/v1/client/requests/documents') && !u.includes('/upload'),
    respond: (cfg) => {
      const body = parseDemoBody(cfg)
      demoOverlay.requestedDocs.push({
        driverId: String(body.employee_id ?? ''),
        items: Array.isArray(body.items) ? (body.items as string[]) : [],
      })
      return widgetOk('Request sent to the driver. (Demo — no email was sent.)', {
        status: 'executed',
        signing_link_included: Boolean(body.include_signing_link),
      })
    },
  },
  {
    method: 'post',
    match: (u) => u.includes('/v1/client/requests/resend-clearinghouse-consent'),
    respond: () =>
      widgetOk('Consent request sent to the driver. (Demo — no email was sent.)', {
        status: 'executed',
      }),
  },
  {
    method: 'post',
    match: (u) => u.includes('/v1/client/requests/resend-onboarding-link'),
    respond: () =>
      widgetOk('Onboarding link sent to the driver. (Demo — no email was sent.)', {
        status: 'executed',
      }),
  },
  {
    // Billing "Turn this on" for a service the client handles (Plan 3 P8).
    method: 'post',
    match: (u) => u.includes('/v1/client/requests/service-activation'),
    respond: () =>
      widgetOk('Your consultant will follow up to take this back over. (Demo — nobody was notified.)', {
        status: 'pending_approval',
        created: true,
        requested_at: new Date().toISOString(),
      }),
  },
  {
    method: 'post',
    match: (u) => u.includes('/v1/client/requests/add-driver'),
    respond: (cfg) => {
      const body = parseDemoBody(cfg)
      const name =
        [body.first_name, body.last_name].filter(Boolean).join(' ') || 'Driver'
      return widgetOk(
        `${name} added — the onboarding invite is queued. (Demo — nothing was created.)`,
        { status: 'executed' },
      )
    },
  },
  {
    // §396.9(d) correction certification — the guided tour demonstrates the
    // sign-off flow (Dugger, 2026-07-31). Without a handler this POST would
    // fall through and the server's staff-read-only 403 would surface as an
    // error mid-demo: safe, but it looks broken. Answered locally instead, so
    // nothing is ever signed.
    //
    // NOTE this is the demo counterpart of the `|| auth.demoMode` on the
    // Certify button in RoadsideInspectionsView — real §396.9(d)(3)(i) signing
    // stays client-only and server-enforced; see that file's comment.
    method: 'post',
    match: (u) => /\/v1\/client\/inspections\/[^/]+\/certify$/.test(u.split('?')[0] ?? u),
    respond: (cfg) => {
      const body = parseDemoBody(cfg)
      const who = String(body.signer_name || 'the carrier official').trim()
      return widgetOk(
        `Correction certification recorded for ${who}. (Demo — nothing was signed or filed.)`,
        { status: 'executed' },
      )
    },
  },
  {
    // Mark an inspection reviewed — same reasoning as the certify handler above.
    method: 'post',
    match: (u) => /\/v1\/client\/inspections\/[^/]+\/review$/.test(u.split('?')[0] ?? u),
    respond: () =>
      widgetOk('Inspection marked reviewed. (Demo — nothing was recorded.)', {
        status: 'executed',
      }),
  },
  {
    // Log an accident — fabricate a full AccidentRow; the registry GET merge
    // (adapter.ts) makes it appear in the table after fetchRegistry().
    method: 'post',
    match: (u) => /\/v1\/client\/accidents$/.test(u.split('?')[0] ?? u),
    respond: (cfg) => {
      const body = parseDemoBody(cfg)
      const now = new Date().toISOString()
      const row: DemoAccident = {
        id: nextDemoId(),
        accident_date: String(body.accident_date ?? now.slice(0, 10)),
        accident_time: (body.accident_time as string) || null,
        location_city: (body.location_city as string) || null,
        location_state: (body.location_state as string) || null,
        driver_id: (body.driver_id as string) || null,
        driver_name: demoDriverName(body.driver_id),
        vehicle_vin: (body.vehicle_vin as string) || null,
        fatality_count: Number(body.fatality_count ?? 0),
        injury_count: Number(body.injury_count ?? 0),
        disabling_damage_tow: Boolean(body.disabling_damage_tow),
        hazmat_released: Boolean(body.hazmat_released),
        // FMCSA-recordable: fatality, injury, or disabling-damage tow-away
        recordable:
          Number(body.fatality_count ?? 0) > 0 ||
          Number(body.injury_count ?? 0) > 0 ||
          Boolean(body.disabling_damage_tow),
        notes: (body.notes as string) || null,
        crash_report: null,
        created_at: now,
        updated_at: now,
      }
      demoOverlay.accidents.unshift(row)
      return row
    },
  },
  {
    // Settings → MVR PDF style: a harmless preference PATCH. Simulated so the
    // Settings page is demoable instead of double-toasting a 403 revert.
    // SettingsView only checks status === 'ok' and keeps its own local state.
    method: 'patch',
    match: (u) => u.includes('/v1/client/mvr-style'),
    respond: (cfg) => {
      const body = parseDemoBody(cfg)
      return widgetOk('MVR style updated. (Demo — nothing was saved.)', {
        mvr_pdf_style: body.mvr_pdf_style ?? null,
      })
    },
  },
  {
    // Attach a crash report to a DEMO-fabricated accident: record the metadata
    // so the row flips to "View" after fetchRegistry(), and stash the actual
    // File so the demo blob adapter (api.ts) can serve its bytes back on View.
    // Real accidents deliberately fall through → 403 backstop.
    method: 'post',
    match: (u) => /\/v1\/client\/accidents\/demo-[^/]+\/crash-report/.test(u),
    respond: (cfg) => {
      const url = cfg.url || ''
      const id = url.split('/').slice(-2)[0] ?? ''
      const body = parseDemoBody(cfg)
      const file = body.file as File | undefined
      const existing = demoOverlay.accidents.find((a) => a.id === id)
      if (existing && file) {
        existing.crash_report = {
          filename: file.name || 'crash-report.pdf',
          mime_type: file.type || null,
          file_size: file.size || null,
          uploaded_at: new Date().toISOString(),
          uploaded_by: null,
        }
        demoOverlay.crashReportFiles[id] = file
      }
      return existing ?? { id }
    },
  },
  {
    // Edit a DEMO-fabricated accident only (id starts with "demo-"). Editing a
    // REAL accident in demo mode deliberately falls through → 403 backstop.
    method: 'put',
    match: (u) => /\/v1\/client\/accidents\/demo-/.test(u),
    respond: (cfg) => {
      const url = cfg.url || ''
      const id = url.slice(url.lastIndexOf('/') + 1)
      const body = parseDemoBody(cfg)
      const existing = demoOverlay.accidents.find((a) => a.id === id)
      if (existing) {
        Object.assign(existing, body, {
          id,
          updated_at: new Date().toISOString(),
          // Keep the rendered name in sync with a (re)assigned driver — the
          // real payload never carries driver_name (assign sends {driver_id}
          // alone; the edit drawer sends driver_id within the full payload).
          driver_name:
            body.driver_id === undefined
              ? existing.driver_name
              : demoDriverName(body.driver_id),
          // Recompute recordable only when the PUT actually carries the
          // recordability fields — a partial body (the assign flow) must not
          // clobber it to false.
          recordable:
            body.fatality_count === undefined &&
            body.injury_count === undefined &&
            body.disabling_damage_tow === undefined
              ? existing.recordable
              : Number(body.fatality_count ?? 0) > 0 ||
                Number(body.injury_count ?? 0) > 0 ||
                Boolean(body.disabling_damage_tow),
        })
      }
      return existing ?? { id }
    },
  },
]

export function findDemoHandler(
  method: string,
  url: string,
): DemoHandler | undefined {
  const m = method.toLowerCase()
  return demoHandlers.find((h) => h.method === m && h.match(url))
}
